import { Appearance, Inventory } from "game-common/models"
import { Container, Graphics, Sprite } from "pixi.js"

import { ClientGameLogic } from "../../../../client_game_logic"
import { GuiManager } from "../gui_manager"
import { BuildPage } from "./character_creation/build_page"
import { BuilderHost, BuilderPage, NavButtonMeta, PageId } from "./character_creation/page"
import { ControlsPage, IntroPage, JourneyPage } from "./character_creation/story_pages"
import {
    ACCENT,
    BACKDROP,
    CONTENT_H,
    CONTENT_W,
    FOOTER_H,
    HEADER_H,
    RIM,
    RIM_DIM,
    TEXT,
    TEXT_DIM,
} from "./character_creation/theme"
import { PillButton, capsText, lensRule, text } from "./character_creation/widgets"
import { FullScreenModal } from "./fullscreen_modal"
import { stageViewport } from "../../../../canvas_layout"
import { setContentWidth } from "./character_creation/theme"
import { BLAZE, EMBER, EmberDrift, flicker, glowSprite, vignetteTexture } from "./ember_atmosphere"
import { ensureIntroFont } from "../intro/intro_theme"

// The character-creation screen.
//
// Four pages share one frame -- a header, the page's body, and a footer of actions the page decides
// on: the exile that opens the game, the character forge itself, the briefing, and the controls
// reference. The forge is the substance of it; see character_creation/build_page.ts.
//
// The screen is only repainted when something actually changes (a page switch, a palette click, the
// player data finally arriving), rather than on every player-stat update the HUD receives -- the
// pages hold live state (which trait is open, how far the intro has typed) that a blind rebuild
// would throw away.

/** The road the player is on, as the header's trail shows it. Controls is a side room, not a step. */
const TRAIL: { id: PageId; label: string }[] = [
    { id: "intro", label: "Exile" },
    { id: "build", label: "Forge" },
    { id: "journey", label: "Journey" },
]

/**
 * The hall the screen is set in: dark, lit from the floor by a fire the player cannot see, with embers
 * drifting up through it. Built ONCE and carried from repaint to repaint -- the pages are rebuilt on
 * every palette click, and embers that restarted from the floor each time would stutter.
 */
class BuilderAtmosphere extends Container {
    private floor = new Graphics()
    private fire = glowSprite(BLAZE, 1, 1, 0.5)
    private fireCore = glowSprite(EMBER, 1, 1, 0.28)
    private vignette = new Sprite(vignetteTexture())
    private embers: EmberDrift
    private ms = 0
    private w = 0
    private h = 0

    constructor() {
        super()
        this.addChild(this.floor)
        this.addChild(this.fire)
        this.addChild(this.fireCore)
        this.addChild(this.vignette)
        this.embers = new EmberDrift({
            width: CONTENT_W,
            height: CONTENT_H,
            count: 22,
            spawnBand: 0.25,
            minSize: 3,
            maxSize: 7,
            minRise: 10,
            maxRise: 26,
            sway: 16,
            minLife: 5,
            maxLife: 10,
            alpha: 0.8,
        })
        this.addChild(this.embers)
    }

    /** Cover the whole screen, out past the dialog's padding. */
    layout = (width: number, height: number) => {
        if (width === this.w && height === this.h) {
            return
        }
        this.w = width
        this.h = height
        this.floor.clear()
        this.floor.beginFill(BACKDROP, 1)
        this.floor.drawRect(-12, -12, width, height)
        this.floor.endFill()
        this.vignette.position.set(-12, -12)
        this.vignette.width = width
        this.vignette.height = height
        this.embers.position.set(-12, -12)
        this.embers.resize(width, height)
        this.animate(0)
    }

    animate = (dtMs: number) => {
        this.ms += dtMs
        const { w, h, ms } = this
        this.embers.update(dtMs)
        // Firelight welling up from below the floor of the screen, breathing.
        this.fire.position.set(w / 2 - 12, h - 12)
        this.fire.width = w * 1.25
        this.fire.height = h * 0.9 * (1 + 0.04 * flicker(ms, 4))
        this.fire.alpha = 0.5 * (1 + 0.1 * flicker(ms))
        this.fireCore.position.set(w / 2 - 12, h)
        this.fireCore.width = w * 0.6
        this.fireCore.height = h * 0.32
        this.fireCore.alpha = 0.28 * (1 + 0.18 * flicker(ms, 2.1))
    }
}

export class CharacterBuilder extends FullScreenModal implements BuilderHost {
    logic: ClientGameLogic

    private pages: Map<PageId, BuilderPage> = new Map()
    private currentPageId: PageId = "intro"
    private navButtons: NavButtonMeta[] = []
    private initialized = false
    private dirty = true
    private wasOpen = false
    private viewportHeight = 0
    private refreshTimer: any
    // A repaint only plays the entrance when the body is genuinely new -- opening the screen, or
    // moving to another page. An in-place repaint (a palette click, a pager step) redraws the same
    // page, and bringing that in from nothing is read as the whole screen blinking.
    private fadeNextPaint = true
    private atmosphere = new BuilderAtmosphere()
    private fontRequested = false

    constructor(guiManager: GuiManager, logic: ClientGameLogic) {
        super(guiManager, {
            opaque: true,
            unescapable: true,
        })
        this.logic = logic
    }

    // The HUD calls this whenever the player entity changes. Rebuild only when the screen has
    // something new to show -- but keep retrying initialization, because the first call usually
    // arrives before the player's appearance and starting kit have.
    update = () => {
        // Opening (toggle -> update) always paints, even if nothing changed since it last closed.
        const open = this.isOpen()
        if (open && !this.wasOpen) {
            this.dirty = true
            this.fadeNextPaint = true
        }
        this.wasOpen = open

        if (!this.initialized && !this.initialize()) {
            return
        }
        if (!this.dirty) {
            return
        }
        this.dirty = false
        this.rebuildSurface()
    }

    // Frame hook (driven by GuiManager.updateTimer) for the page's own animation: the hall's firelight
    // and embers, the entrance, the forge's stage, the intro's typewriter.
    animate = (dtMs: number) => {
        if (!this.isOpen()) {
            return
        }
        if (CONTENT_W !== stageViewport().width - 20 || this.viewportHeight !== stageViewport().height) {
            this.dirty = true
            this.update()
        }
        this.atmosphere.animate(dtMs)
        const page = this.pages.get(this.currentPageId)
        page?.tickEntrance(dtMs)
        page?.animate(dtMs)
    }

    private initialize = (): boolean => {
        const player = this.guiManager.getPlayer()

        if (!player?.appearance || player.inventory?.items?.length < 1) {
            return false
        }

        // The headings and prose are set in Jost. It is normally resolved already -- the login screen
        // waits on it -- but a player who never saw that screen (the local name gate, an embed that
        // boots straight in) would get Verdana rasters, so ask, and repaint once it is here.
        if (!this.fontRequested) {
            this.fontRequested = true
            ensureIntroFont().then(() => this.isOpen() && this.refresh())
        }

        const register = (page: BuilderPage) => {
            page.host = this
            this.pages.set(page.pageId, page)
        }

        register(new IntroPage())
        register(new BuildPage(player))
        register(new JourneyPage())
        register(new ControlsPage())

        this.resetNavButtons()
        this.initialized = true
        return true
    }

    // --- BuilderHost -------------------------------------------------------------------------

    // Repaint, but not from inside the click that asked for it: a palette click would otherwise tear
    // down the very chip pixi is still dispatching the event on. Coalesces a burst into one repaint.
    refresh = () => {
        this.dirty = true
        if (this.refreshTimer !== undefined) {
            return
        }
        this.refreshTimer = setTimeout(() => {
            this.refreshTimer = undefined
            if (this.isOpen()) {
                this.update()
            }
        }, 0)
    }

    setNavButtons = (buttons: NavButtonMeta[]) => {
        this.navButtons = buttons
    }

    resetNavButtons = () => {
        this.navButtons = [
            {
                title: "Edit character",
                callback: () => this.switchPage("build"),
            },
            {
                title: "Review controls",
                callback: () => this.switchPage("controls"),
            },
            {
                title: "Let's go! ▸",
                primary: true,
                callback: () => this.close(),
            },
        ]
    }

    submitCharacter = (appearance: Appearance, inventory: Inventory) => {
        this.logic.playerController.client.saveAppearance(appearance, inventory)
    }

    switchPage = (pageId: PageId) => {
        if (pageId === this.currentPageId) {
            return
        }
        this.pages.get(this.currentPageId)?.tearDown()
        this.currentPageId = pageId
        this.fadeNextPaint = true
        this.refresh()
    }

    // --- rendering ---------------------------------------------------------------------------

    createContent = (): Graphics => {
        const main = new Graphics()
        const viewport = stageViewport()
        this.viewportHeight = viewport.height
        setContentWidth(viewport.width - 20)

        this.atmosphere.layout(CONTENT_W + 24, Math.max(CONTENT_H + 44, viewport.height + 24))
        main.addChild(this.atmosphere)

        if (!this.initialized && !this.initialize()) {
            // Player data hasn't landed yet; the next update() tries again.
            return main
        }

        const page = this.pages.get(this.currentPageId)
        page.build()
        main.addChild(page)

        const heading = this.drawHeader(main, page)
        const footer = this.drawFooter(main)

        // On a page switch the page brings itself in, piece by piece (see BuilderPage.reveal), with
        // the header's title and the footer riding along. A repaint of the page you are already on
        // has to land instantly -- the whole rebuild happens inside one task, so swapping the
        // children is invisible unless something animates them.
        page.alpha = 1
        if (this.fadeNextPaint) {
            this.fadeNextPaint = false
            page.reveal(heading, 0, { rise: 6 })
            page.reveal(footer, 220, { rise: 8 })
            page.startEntrance()
        } else {
            page.finishEntrance()
        }

        return main
    }

    private drawHeader = (g: Graphics, page: BuilderPage): Container => {
        const heading = capsText(page.title, { role: "compactTitle", color: ACCENT, size: 17 })
        heading.x = 2
        heading.y = 3
        g.addChild(heading)

        if (page.hint) {
            const hint = text(page.hint, { role: "compactCaption", color: TEXT_DIM })
            hint.x = CONTENT_W - hint.width - 2
            hint.y = 10
            hint.alpha = 0.75
            g.addChild(hint)
        }

        this.drawTrail(g, page.pageId)

        // A bronze base with the gold lens swelling out of its middle -- the same mark the login
        // wordmark draws under itself, so the two screens read as one sequence.
        const rule = new Graphics()
        rule.beginFill(RIM_DIM, 0.45)
        rule.drawRect(0, HEADER_H - 5, CONTENT_W, 1)
        rule.endFill()
        lensRule(rule, CONTENT_W / 2, HEADER_H - 4.5, CONTENT_W * 0.36)
        g.addChild(rule)

        return heading
    }

    /**
     * Exile -> Forge -> Journey, centred in the header: where the player is, what is behind them and
     * what is ahead. Small and still -- a sense of a road, not a progress bar to race.
     */
    private drawTrail = (g: Graphics, current: PageId) => {
        const at = TRAIL.findIndex(step => step.id === current)
        if (at < 0) {
            return
        }
        const trail = new Container()
        const line = new Graphics()
        trail.addChild(line)
        const cy = 13
        const gap = 26
        let x = 0
        // Where each stop's dot sits and where its label ends, to run the road between them.
        const stops: { x: number; labelRight: number }[] = []
        TRAIL.forEach((step, i) => {
            const isCurrent = i === at
            const done = i < at
            const dot = new Graphics()
            if (isCurrent) {
                dot.beginFill(RIM, 0.18)
                dot.drawCircle(0, 0, 9)
                dot.endFill()
                dot.lineStyle(1.5, ACCENT, 1)
                dot.beginFill(BACKDROP, 1)
                dot.drawCircle(0, 0, 5)
                dot.endFill()
                dot.lineStyle(0)
                dot.beginFill(ACCENT, 1)
                dot.drawCircle(0, 0, 2.5)
                dot.endFill()
            } else if (done) {
                dot.beginFill(RIM, 0.9)
                dot.drawCircle(0, 0, 3.5)
                dot.endFill()
            } else {
                dot.lineStyle(1, RIM_DIM, 0.9)
                dot.beginFill(BACKDROP, 1)
                dot.drawCircle(0, 0, 3.5)
                dot.endFill()
                dot.lineStyle(0)
            }
            dot.x = x
            dot.y = cy
            trail.addChild(dot)

            const label = text(step.label, {
                role: "compactCaption",
                color: isCurrent ? ACCENT : done ? TEXT : TEXT_DIM,
                display: true,
                weight: isCurrent ? "500" : "400",
                size: 11,
            })
            label.alpha = isCurrent ? 1 : done ? 0.75 : 0.55
            label.x = x + 9
            label.y = Math.round(cy - label.height / 2)
            trail.addChild(label)
            stops.push({ x, labelRight: label.x + label.width })
            x = label.x + label.width + gap
        })
        // The road between the stops: walked in gold, ahead in bronze.
        for (let i = 0; i < stops.length - 1; i++) {
            const left = stops[i].labelRight + 6
            const right = stops[i + 1].x - 8
            line.beginFill(i < at ? RIM : RIM_DIM, i < at ? 0.7 : 0.5)
            line.drawRect(left, cy - 0.5, Math.max(0, right - left), 1)
            line.endFill()
        }
        trail.x = Math.round(CONTENT_W / 2 - trail.width / 2)
        g.addChild(trail)
    }

    private drawFooter = (g: Graphics): Container => {
        const y = CONTENT_H - FOOTER_H
        const footer = new Container()

        const rule = new Graphics()
        rule.beginFill(RIM_DIM, 0.45)
        rule.drawRect(0, y, CONTENT_W, 1)
        rule.endFill()
        g.addChild(rule)

        const buttonY = y + 10
        let x = 2
        const primary = this.navButtons.filter(meta => meta.primary)
        const secondary = this.navButtons.filter(meta => !meta.primary)

        secondary.forEach(meta => {
            const button = new PillButton({ label: meta.title, onClick: meta.callback })
            button.x = x
            button.y = buttonY
            footer.addChild(button)
            x += button.boxWidth + 8
        })

        let right = CONTENT_W - 2
        primary
            .slice()
            .reverse()
            .forEach(meta => {
                const button = new PillButton({ label: meta.title, primary: true, onClick: meta.callback })
                button.x = right - button.boxWidth
                button.y = buttonY
                footer.addChild(button)
                right -= button.boxWidth + 8
            })

        g.addChild(footer)
        return footer
    }
}
